import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getStoredLoginIdentifier } from '../auth/sessionToken';
import { useAuth } from '../auth/AuthContext';
import { useHeaderDisplayName } from '../hooks/useHeaderDisplayName';
import { useProfileAvatarSrc } from '../hooks/useProfileAvatarSrc';
import { notifySocialPostsUpdated } from '../hooks/useSocialPosts';
import { useI18n } from '../i18n';
import type { MessageKey } from '../i18n/messages';
import { useAppNavigation } from '../navigation/AppNavigationContext';
import { resolveSocialStripUserLabel } from '../utils/headerDisplayName';
import { addSocialPost, resolveStoryAvatarForPublish } from '../utils/socialPostsStorage';
import { prepareSocialPostImageBlob } from '../utils/socialPostMediaPrepare';
import { ImageSourcePicker, type ImageSourcePickerHandle } from './ImageSourcePicker';
import styles from './SocialPostComposer.module.css';

const SOCIAL_COMPOSER_ERROR_KEYS: Record<string, MessageKey> = {
  'social-missing-media': 'social.composer.error.social-missing-media',
  'social-image-type': 'social.composer.error.social-image-type',
  'social-image-too-large': 'social.composer.error.social-image-too-large',
  'social-save-failed': 'social.composer.error.social-save-failed',
  'social-post-limit': 'social.composer.error.social-post-limit',
  'social-server-error': 'social.composer.error.social-server-error',
};

type SocialPostComposerProps = {
  /** Image already chosen from the + button (user-gesture file pick). */
  imageFile: File;
  /** Caption typed in the story picker before the photo was chosen. */
  initialCaption?: string;
  onClose: () => void;
};

export function SocialPostComposer({
  imageFile: initialImageFile,
  initialCaption = '',
  onClose,
}: SocialPostComposerProps) {
  const { t } = useI18n();
  const { isLoggedIn } = useAuth();
  const { openAuth } = useAppNavigation();
  const headerDisplayName = useHeaderDisplayName();
  const userAvatarSrc = useProfileAvatarSrc();
  const formId = useId();
  const imagePickerRef = useRef<ImageSourcePickerHandle>(null);

  const [text, setText] = useState(initialCaption);
  const [imageFile, setImageFile] = useState<File>(initialImageFile);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setImageFile(initialImageFile);
  }, [initialImageFile]);

  useEffect(() => {
    setText(initialCaption);
  }, [initialCaption]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  useEffect(() => {
    const url = URL.createObjectURL(imageFile);
    setImagePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  const localizeError = useCallback(
    (code: string): string => {
      const key = SOCIAL_COMPOSER_ERROR_KEYS[code];
      if (!key) {
        return t('social.composer.error.generic');
      }
      return t(key);
    },
    [t],
  );

  const onChangeImage = useCallback(() => {
    imagePickerRef.current?.open();
  }, []);

  const onImagePicked = useCallback((file: File) => {
    setError(null);
    setImageFile(file);
  }, []);

  const onSubmit = useCallback(async () => {
    if (!isLoggedIn) {
      openAuth('login');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const userId = getStoredLoginIdentifier()?.trim() || 'local-user';
      const profileFallback = t('social.yourProfile');
      const userLabel = resolveSocialStripUserLabel(
        headerDisplayName !== t('header.guest') ? headerDisplayName : null,
        profileFallback,
      );
      const storedAvatarSrc = await resolveStoryAvatarForPublish(userAvatarSrc);
      const preparedImage = await prepareSocialPostImageBlob(imageFile);

      await addSocialPost({
        userId,
        userLabel,
        userAvatarSrc: storedAvatarSrc,
        text,
        mediaKind: 'imageAudio',
        imageFile: preparedImage,
      });

      notifySocialPostsUpdated();
      onClose();
    } catch (err) {
      const code = err instanceof Error ? err.message : 'generic';
      setError(localizeError(code));
    } finally {
      setSubmitting(false);
    }
  }, [
    isLoggedIn,
    openAuth,
    imageFile,
    localizeError,
    headerDisplayName,
    t,
    userAvatarSrc,
    text,
    onClose,
  ]);

  if (typeof document === 'undefined') {
    return null;
  }

  const messageRtl = /[\u0590-\u05FF]/.test(text);

  return createPortal(
    <div className={styles.overlay} role="presentation" onClick={onClose}>
      <div
        className={styles.sheet}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${formId}-title`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.headerRow}>
          <h2 id={`${formId}-title`} className={styles.title}>
            {t('social.composer.title')}
          </h2>
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label={t('common.close')}>
            ×
          </button>
        </div>

        <p className={styles.hint}>{t('social.composer.expiryHint')}</p>

        {imagePreview ? (
          <img src={imagePreview} alt="" className={styles.imagePreview} />
        ) : null}

        <button type="button" className={styles.pickButton} onClick={onChangeImage} disabled={submitting}>
          {t('social.composer.changeImage')}
        </button>

        <label className={styles.field} htmlFor={`${formId}-text`}>
          <span className={styles.label}>{t('social.composer.message')}</span>
          <textarea
            id={`${formId}-text`}
            className={[styles.textarea, messageRtl ? styles.textareaRtl : ''].filter(Boolean).join(' ')}
            dir={messageRtl ? 'rtl' : 'ltr'}
            lang={messageRtl ? 'he' : undefined}
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder={t('social.composer.messagePlaceholder')}
            rows={3}
            maxLength={500}
          />
        </label>

        <ImageSourcePicker ref={imagePickerRef} onFileSelected={onImagePicked} />

        {error ? (
          <p className={styles.error} role="alert">
            {error}
          </p>
        ) : null}

        <button
          type="button"
          className={styles.publishButton}
          disabled={submitting}
          onClick={() => void onSubmit()}
        >
          {submitting ? t('social.composer.publishing') : t('social.composer.publish')}
        </button>
      </div>
    </div>,
    document.body,
  );
}
